import {
  EmptyState,
  FlexColumn,
  FlexRow,
  FullContainer,
  Loading,
  MaterialIcon,
  StyledSearchBar,
  useDebouncedSearch,
  useLoading,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { LabelMedium, LabelSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { DEFAULT_LIST_ITEM_HEIGHT } from '../../../constants/app';
import { useUpdateEffect } from '../../../hooks/useCustomEffects';
import { useStyles } from '../../../hooks/useStyles';
import { getJobSkills } from '../../../services/jobsService';
import { Skill } from '../../../types/jobs';
import { useJobSkills } from '../hooks/useJobSkills';
import SkillItem from './SkillItem';

type Props = {
  assignedSkills: Skill[];
  onAssignedChange: (skills: Skill[]) => void;
  required?: boolean;
};
export function SkillListPicker({ assignedSkills, onAssignedChange, required }: Readonly<Props>) {
  const { theme, css, classes } = useStyles();
  const [selectedSkills, setSelectedSkills] = useState<Skill[]>();
  const [unselectedSkills, setUnselectedSkills] = useState<Skill[]>();
  const [unselectedSkillHasNext, setUnselectedSkillHasNext] = useState(false);
  const [unselectedSkillPage, setUnselectedSkillPage] = useState(1);
  const { getAvailableSkills } = useJobSkills();
  const {
    startLoading: startUnselectedSkillLoading,
    stopLoading: stopUnselectedSkillLoading,
    loading: unselectedSkillLoading,
  } = useLoading();
  const { search, setSearch } = useDebouncedSearch(useState<string>());
  const skillSelectedCount = selectedSkills?.length ?? 0;

  const isSkillsUnselectLoaded = (index: number) => !unselectedSkillHasNext || index < (unselectedSkills?.length || 0);
  const skillUnselectedCount = unselectedSkillHasNext
    ? (unselectedSkills?.length ?? 0) + 1
    : unselectedSkills?.length ?? 0;
  const loadMoreUnselectSkills =
    unselectedSkillLoading || !unselectedSkillHasNext
      ? () => {}
      : () => {
          setUnselectedSkillPage(unselectedSkillPage + 1);
        };

  const getSelectedSkills = () => {
    if (search) {
      setSelectedSkills(assignedSkills.filter((skill) => skill.name.toLowerCase().includes(search.toLowerCase())));
    } else {
      setSelectedSkills(assignedSkills);
    }
  };

  useEffect(() => {
    getSelectedSkills();
    if (unselectedSkills) {
      setUnselectedSkills(getAvailableSkills(unselectedSkills, assignedSkills));
    }
  }, [assignedSkills]);

  useUpdateEffect(() => {
    setUnselectedSkillPage(0);
    getSelectedSkills();
  }, [search]);

  useEffect(() => {
    if (unselectedSkillPage === 0) {
      setUnselectedSkillPage(1);
      return;
    }
    const loadUnselectedSkills = async () => {
      startUnselectedSkillLoading();
      const { paginatedResponse, error } = await getJobSkills(unselectedSkillPage, search, 'active');
      if (paginatedResponse && !error) {
        if (unselectedSkillPage > 1 && unselectedSkills) {
          setUnselectedSkills(getAvailableSkills([...unselectedSkills, ...paginatedResponse.items], assignedSkills));
          setUnselectedSkillHasNext(paginatedResponse.hasNext);
        } else {
          setUnselectedSkills(getAvailableSkills(paginatedResponse.items, assignedSkills));
          setUnselectedSkillHasNext(paginatedResponse.hasNext);
        }
      } else {
        // TODO error action
        setUnselectedSkills([]);
        setUnselectedSkillHasNext(false);
      }
      stopUnselectedSkillLoading();
    };

    loadUnselectedSkills();
  }, [unselectedSkillPage]);

  if (!selectedSkills && !unselectedSkills) return null;

  const onSelectSkill = (skill: Skill) => {
    const skillsSelection = [...assignedSkills, skill];
    onAssignedChange(skillsSelection);
  };

  const onDeselectSkill = (skill: Skill) => {
    const skillsSelection = assignedSkills?.filter((assignedSkill) => assignedSkill.slug !== skill.slug);
    onAssignedChange(skillsSelection || []);
    setUnselectedSkills((prev) => [...prev!, skill]);
  };

  const onSelectAllSkills = () => {
    onAssignedChange([...(assignedSkills || []), ...(unselectedSkills || [])]);
    setSearch('');
  };

  const onDeselectAllSkills = () => {
    if (search) {
      const skillsSelection = assignedSkills?.filter(
        (skill) => !selectedSkills?.some((selectedSkill) => selectedSkill.slug === skill.slug)
      );
      const skillsUnselection = assignedSkills?.filter((skill) =>
        selectedSkills?.some((selectedSkill) => selectedSkill.slug === skill.slug)
      );
      onAssignedChange(skillsSelection || []);
      setUnselectedSkills((prev) => [...prev!, ...skillsUnselection]);
    } else {
      onAssignedChange([]);
      setUnselectedSkills((prev) => [...prev!, ...assignedSkills]);
    }
    setSearch('');
  };

  return (
    <FullContainer
      flexible
      classNames={`${classes.flexFill} ${classes.verticalFlex} ${css({ gap: theme.sizing.scale400 })}`}
    >
      <StyledSearchBar
        placeholder="Buscar habilidad"
        name="skills-search"
        value={search}
        onChange={setSearch}
        clearable
        noMargin
      />
      <FlexRow classNames={`${classes.flexFill}`}>
        <FlexColumn classNames={`${classes.flexFill} ${css({ gap: theme.sizing.scale300 })}`}>
          <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'space-between' })}`}>
            <LabelMedium>Disponibles</LabelMedium>
            <Button
              size="mini"
              shape="round"
              kind="tertiary"
              disabled={!unselectedSkills?.length}
              onClick={onSelectAllSkills}
            >
              <MaterialIcon
                name="playlist_add"
                size="mini"
              />
            </Button>
          </FlexRow>
          {unselectedSkills && (
            <>
              {!!unselectedSkills?.length && (
                <ul
                  className={css({
                    margin: 0,
                    paddingLeft: 0,
                    paddingRight: 0,
                    height: '100%',
                  })}
                >
                  <AutoSizer>
                    {({ width, height }) => (
                      <InfiniteLoader
                        isItemLoaded={isSkillsUnselectLoaded}
                        itemCount={skillUnselectedCount}
                        loadMoreItems={loadMoreUnselectSkills}
                      >
                        {({ onItemsRendered, ref }) => (
                          <List
                            height={height ? height : 500}
                            itemCount={skillUnselectedCount}
                            itemSize={() => DEFAULT_LIST_ITEM_HEIGHT}
                            width={width ? width : 200}
                            itemData={{
                              skills: unselectedSkills,
                              type: 'selectable',
                              onClick: onSelectSkill,
                              isLoaded: isSkillsUnselectLoaded,
                            }}
                            onItemsRendered={onItemsRendered}
                            overscanCount={4}
                            ref={ref}
                          >
                            {SkillItem}
                          </List>
                        )}
                      </InfiniteLoader>
                    )}
                  </AutoSizer>
                </ul>
              )}
              {search && !unselectedSkills.length && (
                <EmptyState>
                  <LabelMedium>{'No se encontraron resultados'}</LabelMedium>
                  <LabelSmall>{'Intenta con otra busqueda'}</LabelSmall>
                </EmptyState>
              )}
              {!search && !unselectedSkills.length && (
                <EmptyState>
                  <LabelMedium>{'No tienes ninguna habilidad para asignar'}</LabelMedium>
                </EmptyState>
              )}
              {!unselectedSkills && unselectedSkillLoading && <Loading />}
            </>
          )}
        </FlexColumn>
        <FlexColumn classNames={`${classes.flexFill} ${css({ gap: theme.sizing.scale300 })}`}>
          <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'space-between' })}`}>
            <FlexRow gap={theme.sizing.scale300}>
              <LabelMedium>Asignadas {required && '*'}</LabelMedium>
              <LabelMedium color={theme.colors.contentSecondary}>({assignedSkills.length})</LabelMedium>
            </FlexRow>
            <Button
              size="mini"
              shape="round"
              kind="tertiary"
              disabled={!selectedSkills?.length}
              onClick={onDeselectAllSkills}
            >
              <MaterialIcon
                name="playlist_remove"
                size="mini"
              />
            </Button>
          </FlexRow>
          {selectedSkills && (
            <>
              {!!selectedSkills?.length && (
                <ul
                  className={css({
                    margin: 0,
                    paddingLeft: 0,
                    paddingRight: 0,
                    height: '100%',
                  })}
                >
                  <AutoSizer>
                    {({ width, height }) => (
                      <List
                        height={height ? height : 500}
                        itemCount={skillSelectedCount}
                        itemSize={() => DEFAULT_LIST_ITEM_HEIGHT}
                        width={width ? width : 200}
                        itemData={{
                          skills: selectedSkills,
                          type: 'deselectable',
                          onClick: onDeselectSkill,
                        }}
                        overscanCount={4}
                      >
                        {SkillItem}
                      </List>
                    )}
                  </AutoSizer>
                </ul>
              )}
              {search && !selectedSkills.length && (
                <EmptyState>
                  <LabelMedium>{'No se encontraron resultados'}</LabelMedium>
                  <LabelSmall>{'Intenta con otra busqueda'}</LabelSmall>
                </EmptyState>
              )}
              {!search && !selectedSkills.length && (
                <EmptyState>
                  {required ? (
                    <LabelMedium>{'Debes seleccionar al menos una habilidad'}</LabelMedium>
                  ) : (
                    <LabelMedium>{'No tienes ninguna habilidad asignada'}</LabelMedium>
                  )}
                </EmptyState>
              )}
            </>
          )}
        </FlexColumn>
      </FlexRow>
    </FullContainer>
  );
}
